import Insight from "../insightClass";
import React from "react";
import { insightCBAMMedia } from "../../../assets/assetReference";
import { ContactSubscribe } from "../../../components/Contact/Contact";
export const insight080 = new Insight(
  "26 November 2025",
  {
    pageTitle: "CBAM: market implications for the offshore wind sector",
    metaDescription:
      "WeBridge Insights, CBAM: market implications for the offshore wind sector",
    keyWords:
      "European Offshore Wind Market development, CBAM: market implications for the offshore wind sector",
  },
  {
    src: insightCBAMMedia[2],
    alt: "CBAM",
  },
  "CBAM: market implications for the offshore wind sector",
  "The carbon border adjustment mechanism (CBAM) has major implications for the offshore wind sector.",
  `For steel manufacturers and offshore wind developers, the new CBAM regulations pose significant challenges. Now is the time to understand the challenges – and begin making strategic decisions to mitigate their impact. `,
  <div>
    <strong>WeBridge Insights</strong>
    <h1>CBAM: market implications for the offshore wind sector</h1>
    <h2>
      The carbon border adjustment mechanism (CBAM) has major implications for
      the offshore wind sector.{" "}
    </h2>
    <p className="article-date">26 November 2025</p>
    <br />
    <p>
      For steel manufacturers and offshore wind developers, the new CBAM
      regulations pose significant challenges. Now is the time to understand the
      challenges – and begin making strategic decisions to mitigate their
      impact.{" "}
    </p>

    <h3>Before we start…</h3>
    <p>
      CBAM has a unique impact on every implicated business. Talk to us for
      clear insight on how CBAM will affect your business – and how you could
      use the new regulations to enhance business security.{" "}
      <a
        href="https://www.windenergybridge.com/contact-us"
        target="_blank"
        rel="noopener noreferrer"
      >
        Get in touch
      </a>{" "}
      to start the conversation. Or find out more about{" "}
      <a
        href="https://www.windenergybridge.com/about-us"
        target="_blank"
        rel="noopener noreferrer"
      >
        what we do
      </a>
      .
    </p>

    <h3>CBAM, steel and the offshore wind industry </h3>
    <p>
      We’ve taken a deep dive on the nuances of the CBAM policy in a{" "}
      <a
        href="https://www.windenergybridge.com/insights/cbam-for-offshore-wind:-an-introduction-to-customs-and-border-processes"
        target="_blank"
        rel="noopener noreferrer"
      >
        separate article
      </a>
      . But in short, CBAM obligates European offshore wind developers to pay a
      per-tonne fee on the steel they import from outside of the EU. That
      potentially has a significant impact on non-EU manufacturers of steel
      components for offshore wind, because CBAM negates some of the cost
      savings that developers can realise by importing steel from outside of the
      EU.
    </p>
    <p>
      Yet EU demand for steel isn’t going anywhere. Indeed the EU is the world’s
      largest steel importer. In 2024 alone the EU imported over 38 million
      metric tonnes of steel<sup>2</sup>, with around 2 million tonnes dedicated
      to wind installations – a figure that’s predicted to rise to 4 million
      tonnes by 2030<sup>3</sup> thanks to{" "}
      <a
        href="https://www.windenergybridge.com/insights/carbon-border-adjustment-mechanism-cbam:-what-does-it-mean-for-importing-offshore-wind-infrastructure-from-china"
        target="_blank"
        rel="noopener noreferrer"
      >
        ambitious plans for the growth of offshore wind
      </a>
      .
    </p>
    <p>
      Something has to give. Here’s a quick rundown of the main implications of
      CBAM for the offshore wind sector.
    </p>

    <h3>7 implications of CBAM for the offshore wind sector </h3>
    <h4 className="font-bold">1. Cost inflation</h4>
    <p>
      One of the most obvious impacts of CBAM is the increased costs of goods
      covered by the regulation. Offshore wind developers importing steel and{" "}
      <a
        href="https://www.windenergybridge.com/insights/cbam-for-offshore-wind:-an-introduction-to-customs-and-border-processes#:~:text=The%20CBAM%20covers,expected%20by%202030."
        target="_blank"
        rel="noopener noreferrer"
      >
        certain steel products
      </a>{" "}
      from non-EU countries are required to purchase CBAM certificates that
      reflect the emissions embedded in the production of those materials. These
      certificates will be priced in line with the EU ETS, which is susceptible
      to significant price volatility and likely to increase as the EU bears
      down on its decarbonisation targets.<sup>4</sup> For developers, it has
      the potential to chip away at profit margins that are already facing many
      competing pressures.
    </p>
    <p>
      In efforts to remain economically enticing to EU developers, some non-EU
      exporters may choose to reimburse developers for some of their CBAM fees.
      In such case, non-EU exporters of steel products may partially incorporate
      CBAM costs into their pricing, thereby artificially increasing their unit
      rates and effectively eroding the cost advantage of importing steel from
      outside the EU.
    </p>
    <p>
      There is currently{" "}
      <a
        href="https://academic.oup.com/jel/article/34/1/25/6423116#:~:text=On%20the%20import%20side%2C%20CBAMs%20impose%20a,carbon%20price%20that%20has%20already%20been%20paid."
        target="_blank"
        rel="noopener noreferrer"
      >
        nothing in the CBAM rules<sup>1</sup>
      </a>{" "}
      preventing the vested import/export parties from striking a private
      agreement for CBAM reimbursement. But that seems a reactionary strategy
      with questionable long-term viability. Instead non-EU steel producers and
      manufacturers of offshore wind components should be rethinking their
      production processes to maintain – and ultimately grow – their position in
      European markets.
    </p>

    <h4 className="font-bold">
      2. Enhanced data collection and CBAM reporting demands{" "}
    </h4>
    <p>
      CBAM obligates non-EU steel manufacturers to declare the emissions
      embedded in the production of materials exported to the EU. Greater
      collaboration and clear communication between importers and suppliers is
      crucial to success. So too is suppliers’ ability to gather accurate
      emissions data. Without accurate data importers must use default
      calculations, which are deliberately punitive and increase costs further
      for developers. Steel manufacturers that are unable to demonstrate
      emissions traceability are likely to face a competitive disadvantage in EU
      markets.<sup>5</sup>
    </p>
    <p>
      EU importers submitted their first CBAM reports in January 2024. The
      reports had many technical errors – and there was some confusion about the
      reporting system. Indeed many importers were unaware of their reporting
      obligations.<sup>6</sup> In response to these administrative challenges,
      the European Commission introduced a new regulation in early 2025 aimed at
      simplifying CBAM implementation. The objective was to establish a clearer,
      more efficient regulatory framework for businesses while significantly
      reducing administrative, compliance and reporting burdens. Following a
      political agreement between the European Parliament and the Council in
      June, the simplification regulation officially entered into force in late
      October 2025.<sup>7</sup>
    </p>

    <h4 className="font-bold">
      3. An extension of scope to include downstream steel products
    </h4>
    <p>
      As we have{" "}
      <a
        href="https://www.windenergybridge.com/insights/cbam-for-offshore-wind:-an-introduction-to-customs-and-border-processes"
        target="_blank"
        rel="noopener noreferrer"
      >
        already revealed
      </a>
      , the CBAM covers many steel and iron-based offshore wind components –
      including monopiles, jacket structures and towers. Semi-finished steel and
      major fabricated sections used in offshore wind structures are also
      covered – such as steel plates, tubes, lattice masts and pillars.
    </p>
    <p>
      However, the CBAM is soon likely to include more downstream products
      created from non-EU manufacturers. These could include items such as
      electrical equipment (parts, housing, etc.), walkways and platforms,
      handrails, ladders, cable-mounting systems, fall-arrest systems and so on.
      Because some of these components largely use the same CN code categories
      as other steel/aluminium structures, many are already in CBAM scope.
      However, other items (such as complete generators or gearboxes) are
      currently exempt and would only be included under a downstream expansion.
    </p>
    <p>
      In the recently produced{" "}
      <a
        href="https://single-market-economy.ec.europa.eu/publications/european-steel-and-metals-action-plan_en"
        target="_blank"
        rel="noopener noreferrer"
      >
        European Steel & Metals Action Plan
      </a>
      , regulators stress the need for CBAM to extend its scope to include
      certain steel and aluminium-intensive downstream products. The European
      Commission launched a public consultation in July 2025 to gather
      stakeholder feedback on the proposed expansion of CBAM’s scope. The
      consultation seeks input on the inclusion of specific downstream products,
      the introduction of anti-circumvention measures and the development of
      rules for the electricity sector, as well as on the potential social,
      economic, environmental and administrative impacts of these measures. A
      proposal is expected by the end of 2025.<sup>8</sup>
    </p>
    <p>
      <a
        href="https://www.windenergybridge.com/insights/uk-cfd-ar7:-offshore-wind-outlook-and-supply-chain-challenges"
        target="_blank"
        rel="noopener noreferrer"
      >
        &gt;&gt; UK CfD AR7: Offshore wind outlook and global supply chain
        challenges
      </a>
    </p>

    <h4 className="font-bold">4. Supply chain pressure</h4>
    <p>
      The new CBAM regulations create compliance hurdles for both importers and
      suppliers. The resulting economic and administrative disruption is likely
      to exacerbate the{" "}
      <a
        href="https://www.windenergybridge.com/insights/6-reasons-why-uk-offshore-wind-requires-a-'glocal'-approach"
        target="_blank"
        rel="noopener noreferrer"
      >
        existing supply chain bottlenecks
      </a>{" "}
      that are extant in the offshore wind sector. At the company level, the
      CBAM policy is expected to intensify investor scrutiny of firms’ carbon
      exposure, with stakeholders increasingly demanding transparency regarding
      climate risks and potential CBAM-related liabilities within their
      portfolios.
    </p>

    <h4 className="font-bold">5. Project delays and cancellations </h4>
    <p>
      Additional supply chain pressures and enhanced scrutiny of suppliers’
      emissions exposures is likely to lead to further project delays and
      potential cancellations, which could further undermine efforts to meet
      already overstretched net-zero targets.<sup>9</sup> It’s more important
      than ever that offshore wind developers plan ahead and create
      contingencies for potential delays in order to protect the economic
      integrity of their projects.
    </p>

    <h4 className="font-bold">6. Global ripple effect in carbon pricing</h4>
    <p>
      It’s hoped that the introduction of CBAM will encourage other countries to
      adopt their own emissions pricing systems. And there are powerful
      incentives to do so. Any emissions that have already been taxed in the
      country of origin are subtracted from applicable CBAM import duties.
      Therefore, domestic emissions pricing helps to protect domestic producers
      from losing their place in European markets.<sup>10</sup>
    </p>
    <p>
      Indeed the implementation of domestic emissions pricing policies is{" "}
      <a
        href="https://www.rff.org/publications/reports/how-carbon-border-adjustments-might-drive-global-climate-policy-momentum/#:~:text=Evidence%20is%20growing,and%20fiscal%20revenues"
        target="_blank"
        rel="noopener noreferrer"
      >
        trending upwards
      </a>
      . The UK is finalising{" "}
      <a
        href="https://www.gov.uk/government/publications/factsheet-carbon-border-adjustment-mechanism-cbam/factsheet-carbon-border-adjustment-mechanism"
        target="_blank"
        rel="noopener noreferrer"
      >
        its own version of CBAM
      </a>
      . Canada, the US, Thailand, Brazil and India are also beginning to explore
      similar mechanisms.<sup>10</sup> China, meanwhile, is considering adding
      the steel, cement and aluminium sectors to its ETS ahead of the definitive
      phase of the CBAM from 1<sup>st</sup> January 2026.<sup>10</sup>
    </p>
    <p>
      The more countries that implement emissions pricing, the more economic
      pressure there is on additional countries to follow suit in order to
      preserve their exporters’ access to foreign markets.
    </p>

    <h4 className="font-bold">7. Strained global trade relations</h4>
    <p>
      As the global steel industry adjusts to CBAM, we can expect lower
      carbon-intensity steel products to be redirected towards the EU, while
      carbon-intensive products are exported to other destinations. This
      process, known as carbon shuffling, hints at the formation of a two-tier
      market that will persist until steel producers catch up with the economic
      and environmental imperatives of green steel.
    </p>
    <p>
      Some countries, however – including Brazil, India and South Africa – have
      expressed concerns about the EU’s CBAM, labelling it as protectionism
      disguised as climate action that discriminates against European imports of
      their traded products.<sup>11</sup> Indeed it’s true that CBAM exerts
      commercial pressure on third-country producers to disclose and reduce
      emissions, which many may be unwilling or unable to do. The world’s least
      developed countries often have greater reliance on fossil fuels – and CBAM
      will put greater financial pressure on them than developed nations.
      <sup>6</sup> It’s also true to say that these developing nations have made
      the fewest greenhouse gases but are disproportionately likely to feel the
      consequences.<sup>11</sup>
    </p>
    <p>
      Such moral and ethical quandaries are fuelling{" "}
      <a
        href="https://www.cer.eu/publications/archive/policy-brief/2024/learning-cbams-transitional-impacts-trade"
        target="_blank"
        rel="noopener noreferrer"
      >
        calls
      </a>{" "}
      for the EU to use CBAM duties to help developing countries adopt green
      production technologies.<sup>12</sup> Alternatively the EU could grant
      exemptions or discounts from certificate payments for in-scope CBAM
      materials that have originated from less developed counties.<sup>6</sup>{" "}
      The EU has so far been reluctant to support such proposals, but its stance
      may evolve as the broader implications of the CBAM policy become more
      apparent.
    </p>

    <h3>What does the future hold?</h3>
    <p>
      For now it’s clear that manufacturers and fabricators that can demonstrate
      clean, energy-efficient production are going to be far more attractive to
      EU importers due to the material lessening of CBAM charges. Suppliers that
      can espouse green production processes will also help buyers to fulfil
      other objectives related to corporate social responsibility, which can be
      beneficial for attracting new clients and new investment.
    </p>
    <p>
      <a
        href="https://www.windenergybridge.com/insights/uk-cfd-ar7:-offshore-wind-outlook-and-supply-chain-challenges"
        target="_blank"
        rel="noopener noreferrer"
      >
        &gt;&gt; UK CfD AR7: Offshore wind outlook and global supply chain
        challenges
      </a>
    </p>

    <h3>What does CBAM mean for your business?</h3>
    <p>
      For clear insight on how CBAM will impact your business – and how you can
      use the new rules to bring more security to the future of your business –{" "}
      <a
        href="https://www.windenergybridge.com/contact-us"
        target="_blank"
        rel="noopener noreferrer"
      >
        get in touch
      </a>{" "}
      to arrange a meeting. Or find out more about{" "}
      <a
        href="https://www.windenergybridge.com/about-us"
        target="_blank"
        rel="noopener noreferrer"
      >
        what we do
      </a>
      .
    </p>

    <h3>References</h3>
    <ol className="list-decimal ml-6">
      <li>
        Journal of Environmental Law:{" "}
        <a
          href="https://academic.oup.com/jel/article/34/1/25/6423116"
          target="_blank"
          rel="noopener noreferrer"
        >
          Carbon Border Adjustment Measures: A Straightforward Multi-Purpose
          Climate Change Instrument?
        </a>
      </li>
      <li>
        The International Trade Administration:{" "}
        <a
          href="https://www.trade.gov/media/5479"
          target="_blank"
          rel="noopener noreferrer"
        >
          Steel Trade Balance.
        </a>
      </li>
      <li>
        WindEurope:{" "}
        <a
          href="https://www.linkedin.com/posts/windeurope_steel-is-70-of-a-wind-turbines-mass-and-activity-7302732625213878272-Li9b/"
          target="_blank"
          rel="noopener noreferrer"
        >
          LinkedIn
        </a>
      </li>
      <li>
        Ramboll:{" "}
        <a
          href="https://www.ramboll.com/en-us/insights/decarbonise-for-net-zero/the-carbon-border-adjustment-mechanism-rewiring-trade-for-a-low-carbon-economy"
          target="_blank"
          rel="noopener noreferrer"
        >
          The Carbon Border Adjustment Mechanism: Rewiring trade for a
          low-carbon economy
        </a>
      </li>
      <li>
        Z2:{" "}
        <a
          href="https://www.z2data.com/insights/why-cbam-most-important-regulation-you-dont-know"
          target="_blank"
          rel="noopener noreferrer"
        >
          Why CBAM is the most important regulation you don’t know about
        </a>
      </li>
      <li>
        Environmental & Energy Study Institute:{" "}
        <a
          href="https://www.eesi.org/articles/view/europes-carbon-border-adjustment-mechanism-a-new-age-of-sustainable-trade"
          target="_blank"
          rel="noopener noreferrer"
        >
          Europe’s Carbon Border Adjustment Mechanism: A New Age of Sustainable
          Trade
        </a>
      </li>
      <li>
        Mayer Brown:{" "}
        <a
          href="https://www.mayerbrown.com/en/insights/publications/2025/10/eu-adopts-cbam-simplification-regulation-10-key-amendments-and-challenges-ahead"
          target="_blank"
          rel="noopener noreferrer"
        >
          EU Adopts CBAM Simplification Regulation: 10 Key Amendments and
          Challenges Ahead
        </a>
      </li>
      <li>
        European Commission:{" "}
        <a
          href="https://taxation-customs.ec.europa.eu/news/cbam-public-consultation-extension-cbam-downstream-products-2025-07-02_en"
          target="_blank"
          rel="noopener noreferrer"
        >
          CBAM: Public consultation on the extension of CBAM to downstream
          products
        </a>
      </li>
      <li>
        Recharge: Nordex CEO:{" "}
        <a
          href="https://www.rechargenews.com/markets-and-finance/nordex-ceo-the-eu-border-tax-will-push-up-prices-and-hurt-wind-industry/2-1-1892200"
          target="_blank"
          rel="noopener noreferrer"
        >
          The EU border tax will push up prices and hurt wind industry
        </a>
      </li>
      <li>
        Resources for the Future:{" "}
        <a
          href="https://www.rff.org/publications/reports/how-carbon-border-adjustments-might-drive-global-climate-policy-momentum/"
          target="_blank"
          rel="noopener noreferrer"
        >
          How Carbon Border Adjustments Might Drive Global Climate Policy
          Momentum
        </a>
      </li>
      <li>
        CATO Institute:{" "}
        <a
          href="https://www.cato.org/briefing-paper/legal-issues-european-carbon-border-adjustment-mechanism"
          target="_blank"
          rel="noopener noreferrer"
        >
          Legal Issues with the European Carbon Border Adjustment Mechanism
        </a>
      </li>
      <li>
        Centre for European Reform:{" "}
        <a
          href="https://www.cer.eu/publications/archive/policy-brief/2024/learning-cbams-transitional-impacts-trade"
          target="_blank"
          rel="noopener noreferrer"
        >
          Learning from CBAM's transitional phase: Early impacts on trade and
          climate efforts
        </a>
      </li>
    </ol>

    <br />
    <br />
    <br />
    <p>
      As the global offshore wind sector continues to advance, innovation,
      investments, and supportive policies are driving transformative growth. At{" "}
      <i>
        <b>WeBridge</b>
      </i>
      , we provide the expertise and insights our partners need to confidently
      navigate this evolving landscape.
    </p>
    <h3 style={{ marginTop: "200px" }}>Subscribe</h3>
    <ContactSubscribe />
  </div>,
);
